import { useCallback, useEffect, useRef, useState } from 'react'

interface Message {
  role: 'user' | 'assistant'
  content: string
}

type ChannelMessage =
  | { type: 'ROUTE_CHANGE'; pathname: string }
  | { type: 'POP_OUT' }
  | { type: 'POP_IN' }
  | { type: 'PANEL_CLOSE' }
  | { type: 'MESSAGES_SYNC'; messages: Message[] }
  | { type: 'NAVIGATE'; pathname: string }

const CHANNEL_NAME = 'agent-chat-sync'
const MESSAGES_KEY = 'agentChat_messages'
const POPPED_OUT_KEY = 'agentChat_poppedOut'

const loadMessages = (): Message[] => {
  try {
    const stored = localStorage.getItem(MESSAGES_KEY)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

const persistMessages = (messages: Message[]) => {
  try {
    localStorage.setItem(MESSAGES_KEY, JSON.stringify(messages))
  } catch {
    // localStorage full or unavailable
  }
}

interface UseAgentChatSyncOptions {
  isPopoutWindow: boolean
}

interface UseAgentChatSyncReturn {
  messages: Message[]
  setMessages: (msgs: Message[] | ((prev: Message[]) => Message[])) => void
  remotePathname: string | null
  isPoppedOut: boolean
  popOut: () => void
  popIn: () => void
  closePopout: () => void
  navigateMain: (pathname: string) => void
}

export const useAgentChatSync = ({ isPopoutWindow }: UseAgentChatSyncOptions): UseAgentChatSyncReturn => {
  const [messages, setMessagesRaw] = useState<Message[]>(loadMessages)
  const [remotePathname, setRemotePathname] = useState<string | null>(null)
  const [isPoppedOut, setIsPoppedOut] = useState(
    () => localStorage.getItem(POPPED_OUT_KEY) === 'true'
  )

  const channelRef = useRef<BroadcastChannel | null>(null)
  const suppressSyncRef = useRef(false)

  // Create channel on mount
  useEffect(() => {
    const bc = new BroadcastChannel(CHANNEL_NAME)
    channelRef.current = bc

    bc.onmessage = (event: MessageEvent<ChannelMessage>) => {
      const msg = event.data

      if (msg.type === 'MESSAGES_SYNC') {
        suppressSyncRef.current = true
        setMessagesRaw(msg.messages)
        suppressSyncRef.current = false
      }

      if (msg.type === 'ROUTE_CHANGE' && isPopoutWindow) {
        setRemotePathname(msg.pathname)
      }

      if (msg.type === 'POP_OUT' && !isPopoutWindow) {
        setIsPoppedOut(true)
      }

      if (msg.type === 'POP_IN' && !isPopoutWindow) {
        setIsPoppedOut(false)
      }

      if (msg.type === 'PANEL_CLOSE' && isPopoutWindow) {
        localStorage.setItem(POPPED_OUT_KEY, 'false')
        window.close()
      }
    }

    return () => {
      bc.close()
      channelRef.current = null
    }
  }, [isPopoutWindow])

  // Listen for storage events (fallback for when pop-out closes via browser X)
  useEffect(() => {
    if (isPopoutWindow) return

    const onStorage = (e: StorageEvent) => {
      if (e.key === POPPED_OUT_KEY) {
        setIsPoppedOut(e.newValue === 'true')
      }
      if (e.key === MESSAGES_KEY && e.newValue) {
        try {
          suppressSyncRef.current = true
          setMessagesRaw(JSON.parse(e.newValue))
          suppressSyncRef.current = false
        } catch {
          // ignore parse errors
        }
      }
    }

    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [isPopoutWindow])

  // Wrapped setMessages that persists + broadcasts
  const setMessages = useCallback(
    (updater: Message[] | ((prev: Message[]) => Message[])) => {
      setMessagesRaw(prev => {
        const next = typeof updater === 'function' ? updater(prev) : updater
        persistMessages(next)

        if (!suppressSyncRef.current && channelRef.current) {
          channelRef.current.postMessage({ type: 'MESSAGES_SYNC', messages: next })
        }

        return next
      })
    },
    []
  )

  const popOut = useCallback(() => {
    // Persist current messages before opening
    persistMessages(messages)
    localStorage.setItem(POPPED_OUT_KEY, 'true')

    window.open(
      '/agent-chat',
      'grobotic-agent-chat',
      'width=700,height=600,resizable=yes,scrollbars=yes'
    )

    channelRef.current?.postMessage({ type: 'POP_OUT' })
    setIsPoppedOut(true)
  }, [messages])

  const popIn = useCallback(() => {
    persistMessages(messages)
    localStorage.setItem(POPPED_OUT_KEY, 'false')

    channelRef.current?.postMessage({ type: 'POP_IN' })
    setIsPoppedOut(false)

    if (isPopoutWindow) {
      window.close()
    }
  }, [messages, isPopoutWindow])

  const closePopout = useCallback(() => {
    if (!isPopoutWindow) {
      channelRef.current?.postMessage({ type: 'PANEL_CLOSE' })
    }
  }, [isPopoutWindow])

  const navigateMain = useCallback(
    (pathname: string) => {
      if (isPopoutWindow) {
        channelRef.current?.postMessage({ type: 'NAVIGATE', pathname })
      }
    },
    [isPopoutWindow]
  )

  return { messages, setMessages, remotePathname, isPoppedOut, popOut, popIn, closePopout, navigateMain }
}

export { CHANNEL_NAME, POPPED_OUT_KEY }
export type { Message as AgentChatMessage }
